import Vue from 'vue'
import { hashCode } from '@/utils/string'
import database from '@/utils/database'
import sourceData from './sourceData'

class FilterDefinition {
  constructor (name, label, itemsGetter, mutation, matchFunction, type) {
    this.name = name
    this.label = label
    this.itemsGetter = itemsGetter
    this.mutation = mutation
    this.matchFunction = matchFunction
    this.type = type || 'checkbox'
  }
}

const getCategories = () => Vue.store.getters['destiny/profile/allItemsCategories']
  .map(category => {
    return {
      'hash': category.hash,
      'name': category.displayProperties.name
    }
  })

const getSubtypes = () =>
  [...new Set(Vue.store.state.destiny.profile.items
    .filter(i => i.damageType.id)
    .map(i => i.definition.itemTypeDisplayName))]
    .sort()
    .map(i => {
      return {
        hash: hashCode(i),
        name: i
      }
    })

const getRarity = () =>
  Object.values(database.definitions.DestinyItemTierTypeDefinition)
    .sort((a, b) => {
      return a.index < b.index ? 1 : -1
    })
    .map(tier => {
      return {
        hash: tier.hash,
        name: tier.displayProperties.name
      }
    })
    .filter(tier => Vue.store.state.destiny.profile.items.filter(i => i.tierType.hash === tier.hash).length)

const getModsRarity = () => {
  const allMods = Vue.store.state.destiny.profile.items.filter(i => i.definition.itemType === 19)
  return Object.values(database.definitions.DestinyItemTierTypeDefinition)
    .sort((a, b) => {
      return a.index < b.index ? 1 : -1
    })
    .map(tier => {
      return {
        hash: tier.hash,
        name: tier.displayProperties.name
      }
    })
    .filter(tier => allMods.filter(i => i.tierType.hash === tier.hash).length)
}
const getSets = () => {
  return sourceData.map(({name}) => {
    return {name, hash: hashCode(name)}
  })
}

const getDamageType = () => Object.values(database.definitions.DestinyDamageTypeDefinition)
  .sort((a, b) => a.index < b.index ? -1 : 1)
  .map(damageType => {
    return {
      hash: damageType.hash,
      name: damageType.displayProperties.name,
      icon: damageType.displayProperties.icon
    }
  })

const getVarious = () => Vue.store.state.destiny.filters.variousLabels.map(
  (name, i) => {
    return {hash: i, name}
  }
)

function modsRarityMatch (item, value) {
  const socketRarities = new Set(item.displayableSockets.map(socket => socket.inventory.tierTypeHash))
  return value.find(rarity => socketRarities.has(rarity)) !== undefined
}

function getSourceMatch (item, value) {
  return item.sources.map(source => hashCode(source)).find(code => value.find(needle => needle === code)) !== undefined
}

function variousMatch (item, value) {
  return value.map(index => Vue.store.state.destiny.filters.variousLabels[index])
    .every(label => {
      return (label === 'Duplicated' && item.isDuplicate) ||
        (label === 'Different' && !item.isDuplicate) ||
        (label === 'Favorited' && item.favorited) ||
        (label === 'Unfavorited' && !item.favorited) ||
        (label === 'Equipped' && item.equipped) ||
        (label === 'Unequipped' && !item.equipped) ||
        (label === 'Transferable' && item.isTransferable) ||
        (label === 'Untransferable' && !item.isTransferable) ||
        (label === 'Best duplicates' && item.isBestDuplicate) ||
        (label === 'Worst duplicates' && item.isBestDuplicate === false)
    })
}

function searchMatch (item, value) {
  const tokens = value.toLowerCase().split(' ').map(w => w.trim()).filter(w => w)
  return tokens.every(token => item.checkToken(token))
}

const defaultFilterDefinitions = [
  new FilterDefinition('search', '', null, '', searchMatch),
  new FilterDefinition('categories', 'Categories', getCategories,
    'destiny/filters/updateCategories',
    (item, value) => value.indexOf(item.bucket.hash) !== -1),
  new FilterDefinition('subtypes', 'Weapon subtype', getSubtypes,
    'destiny/filters/updateSubtypes',
    (item, value) => value.indexOf(hashCode(item.definition.itemTypeDisplayName)) !== -1,
    'radio'
  ),
  new FilterDefinition('damageType', 'Damage Type', getDamageType,
    'destiny/filters/updateDamageType',
    (item, value) => value.indexOf(item.damageType.hash) !== -1
  ),
  new FilterDefinition('rarity', 'Rarity', getRarity,
    'destiny/filters/updateRarity',
    (item, value) => value.indexOf(item.tierType.hash) !== -1
  ),
  new FilterDefinition('modsRarity', 'Mods Rarity', getModsRarity,
    'destiny/filters/updateModsRarity', modsRarityMatch),
  new FilterDefinition('sets', 'Source', getSets,
    'destiny/filters/updateSets', getSourceMatch),
  new FilterDefinition('various', 'Various', getVarious,
    'destiny/filters/updateVarious', variousMatch)
]

class Filter {
  constructor (value, filterDefinition) {
    this.value = value
    this.filterDefinition = filterDefinition
  }

  get name () {
    return this.filterDefinition.name
  }

  get label () {
    return this.filterDefinition.label
  }

  get items () {
    return this.filterDefinition.itemsGetter ? this.filterDefinition.itemsGetter() : []
  }

  get model () {
    return this.value
  }

  set model (value) {
    setTimeout(() => {
      const payload = {}
      payload[this.name] = value
      Vue.store.commit(this.filterDefinition.mutation, payload)
    })
  }

  match (item) {
    return this.filterDefinition.matchFunction ? this.filterDefinition.matchFunction(item, this.value) : false
  }

  get display () {
    return this.label ? this.items.filter(({hash}) => this.value.indexOf(hash) !== -1)
        .map(({name}) => name)
      : [this.value]
  }
}

class FiltersCollection {
  constructor (values, filterDefinitions) {
    this.filters = (filterDefinitions || defaultFilterDefinitions)
      .reduce((acc, filterDefinition) => {
        acc[filterDefinition.name] = new Filter(values[filterDefinition.name], filterDefinition)
        return acc
      }, {})
    this.filtersValues = {}
    Object.values(this.filters).forEach(filter => {
      this.filtersValues[filter.name] = values[filter.name]
    })
  }

  static fromExportable (exportable, filterDefinitions) {
    let values
    try {
      values = JSON.parse(atob(exportable.replace(/\n/g, '')))
    } catch (e) {
      values = {search: exportable}
    }

    return new FiltersCollection(
      values,
      filterDefinitions
    )
  }

  get cleanFilters () {
    return Object.entries(this.filtersValues)
      .filter(([k, v]) => v && v.length)
      .reduce((acc, [k, v]) => {
        acc[k] = v
        return acc
      }, {})
  }

  get exportable () {
    return [...btoa(JSON.stringify(this.cleanFilters))].reduce((acc, item, i) => {
      acc[i >> 5] = (acc[i >> 5] || '') + item
      return acc
    }, []).join('\n')
  }

  getValue (filter) {
    return this.filtersValues[filter]
  }

  setValue (filter, value) {
    setTimeout(() => {
      const payload = {}
      payload[filter] = value
      Vue.store.commit(this.filterArguments[filter].mutation, payload)
    })
  }

  filter (items) {
    const toApply = Object.keys(this.cleanFilters).map(key => this.filters[key])
    return items
      .filter(item => toApply.find(filter => !filter.match(item)) === undefined)
  }

  get displayValues () {
    return Object.keys(this.cleanFilters)
      .map(key => [key, this.filters[key].display])
      .reduce((acc, [key, values]) => {
        acc[this.filters[key].label || key] = values
        return acc
      }, {})
  }
}

export default FiltersCollection



// WEBPACK FOOTER //
// ./src/domain/FiltersCollection.js